import { useT } from '@hostesa/i18n';
import { Button, EmptyState, ErrorState, ListToolbar, LoadingState, Pagination } from '@hostesa/ui';
import type { ReactNode } from 'react';

const PAGE_WRAP = 'mx-auto w-full max-w-6xl px-4 py-6 sm:px-8 sm:py-8';

export interface OversightFrameProps {
  testId: string;
  eyebrow?: string;
  title: string;
  description?: string;
  /** Optional right-aligned header content (e.g. compact metric figures). */
  headerTrailing?: ReactNode;

  /** Search input value + handler (the page owns the URL state). */
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  /** Entity-specific filter controls rendered in the toolbar. */
  filterControls?: ReactNode;
  /** True when any filter/search is active (shows the Clear action + filtered-empty). */
  filterActive: boolean;
  onClear: () => void;

  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyIcon?: ReactNode;
  emptyTitle: string;
  emptyDescription?: string;
  emptyFilteredTitle: string;
  emptyFilteredDescription?: string;

  /** The row list (a `Card` wrapping a `<ul>`), rendered only when there are rows. */
  children?: ReactNode;

  /** Pagination — rendered only when `total > 0` and not loading/error. */
  total: number;
  limit: number;
  offset: number;
  onPageChange: (next: { offset: number; limit: number }) => void;
}

/**
 * Shared scaffold for the operator oversight list pages (Tenants / Hotels /
 * Calls). Owns the brand page chrome + the calm loading/error/empty/pagination
 * states; each page supplies its toolbar filters and row rendering. Presentational
 * only — the page owns all state (URL search params).
 */
export function OversightFrame({
  testId,
  eyebrow,
  title,
  description,
  headerTrailing,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filterControls,
  filterActive,
  onClear,
  isLoading,
  isError,
  isEmpty,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyFilteredTitle,
  emptyFilteredDescription,
  children,
  total,
  limit,
  offset,
  onPageChange,
}: OversightFrameProps) {
  const { t } = useT('backoffice');
  return (
    <section data-testid={testId} className={PAGE_WRAP}>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && (
            <p className="mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)]">
              {eyebrow}
            </p>
          )}
          <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
            {title}
          </h1>
          {description && (
            <p className="mt-1 text-sm text-[var(--color-stone-mid)]">{description}</p>
          )}
        </div>
        {headerTrailing}
      </header>

      <ListToolbar
        data-testid={`${testId}-toolbar`}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        trailing={
          filterActive ? (
            <Button data-testid={`${testId}-clear`} variant="ghost" size="sm" onClick={onClear}>
              {t('oversight.clear')}
            </Button>
          ) : undefined
        }
      >
        {filterControls}
      </ListToolbar>

      {isLoading ? (
        <LoadingState data-testid={`${testId}-loading`} title={t('oversight.loading')} />
      ) : isError ? (
        <ErrorState
          data-testid={`${testId}-error`}
          title={t('oversight.error.title')}
          description={t('oversight.error.description')}
        />
      ) : isEmpty ? (
        filterActive ? (
          <EmptyState
            data-testid={`${testId}-empty-filtered`}
            icon={emptyIcon}
            title={emptyFilteredTitle}
            description={emptyFilteredDescription}
            action={
              <Button
                data-testid={`${testId}-clear-empty`}
                variant="secondary"
                size="sm"
                onClick={onClear}
              >
                {t('oversight.clear')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            data-testid={`${testId}-empty`}
            icon={emptyIcon}
            title={emptyTitle}
            description={emptyDescription}
          />
        )
      ) : (
        children
      )}

      {!isLoading && !isError && total > 0 && (
        <Pagination
          data-testid={`${testId}-pagination`}
          total={total}
          limit={limit}
          offset={offset}
          onChange={onPageChange}
          rangeLabel={(from, to, tot) => t('oversight.pagination.range', { from, to, total: tot })}
          prevLabel={t('oversight.pagination.prev')}
          nextLabel={t('oversight.pagination.next')}
          pageSizeLabel={t('oversight.pagination.perPage')}
        />
      )}
    </section>
  );
}
